// src/App.js
// Changes from previous version:
// - Added: import Publisher
// - Added: <Route path="/publisher" element={<Publisher />} />
// - /dashboard now shows clean Overview, /publisher shows analytics

import { lazy, Suspense } from'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from'react-router-dom';
import { Toaster } from'react-hot-toast';
import { AuthProvider } from'./contexts/AuthContext';
import { SidebarProvider } from'./contexts/SidebarContext';
import { ThemeProvider } from'./contexts/ThemeContext';
import ProtectedRoute from'./components/auth/ProtectedRoute';
import DashboardLayout from'./components/layout/DashboardLayout';
import { useAttribution } from'./hooks/useAttribution';

// Auth Pages
import Login from'./pages/Login';
import Register from'./pages/Register';
import ForgotPassword from'./pages/ForgotPassword';
import ResetPassword from'./pages/ResetPassword';

// Dashboard Pages
import AdSenseCallback from'./pages/AdSenseCallback';
import Dashboard from'./pages/Dashboard';
import Publisher from'./pages/Publisher';
import MetaAds from'./pages/MetaAds';
import AdSense from'./pages/AdSense';
import AdSenseUrls from'./pages/AdSenseUrls';
import AdManager from'./pages/AdManager';
import AdManagerUrls from'./pages/AdManagerUrls';
import CustomDomains from'./pages/CustomDomains';
import CustomAccounts from'./pages/CustomAccounts';
// Legacy section-based landing pages. Kept reachable so pages published with
// the old builder keep serving and stay editable; new pages are built with the
// GrapesJS editor under /landing-pages (see FunnelsList kind="page").
import LandingPages from'./pages/LandingPages';
import LandingPageBuilder from'./pages/landing-builder';
import Affiliate from'./pages/Affiliate';
import Support from'./pages/Support';
import Subscribe from'./pages/Subscribe';
import Settings from'./pages/Settings';
import GoogleAds from'./pages/GoogleAds';
import TikTokAds from './pages/TikTokAds';
import Team from './pages/Team';
import Demo from './pages/Demo';
import Shopify from'./pages/Shopify';
import TikTokShop from'./pages/TikTokShop';
import EcommerceDashboard from'./pages/EcommerceDashboard';
import Onboarding from'./pages/Onboarding';
import TestDetail from'./pages/TestDetail';
import TestAnalytics from'./pages/TestAnalytics';
import EmailMarketing from'./pages/EmailMarketing';

// Funnels + standalone landing pages. Both are rows in the `funnels` table
// distinguished by `kind`, so FunnelsList and FunnelDetail serve both.
import FunnelsList from'./pages/funnels/FunnelsList';
import FunnelDetail from'./pages/funnels/FunnelDetail';
import Results from'./pages/funnels/Results';
import AbTests from'./pages/AbTests';

// HapiGuard
import HapiGuard from'./pages/HapiGuard';

// Admin Pages
import Admin from'./pages/Admin';
import SystemAdmin from'./pages/admin/SystemAdmin';

import'./App.css';

// GrapesJS is ~250KB gz — lazy-load the editor so it stays out of the main bundle.
const FunnelEditor = lazy(() => import('./pages/funnels/FunnelEditor'));

const EditorFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

// Mounts inside Router so window.location is available
function AttributionCapture() {
  useAttribution();
  return null;
}

function App() {
 return (
 <AuthProvider>
 <ThemeProvider>
 <SidebarProvider>
 <Router>
          <AttributionCapture />
 <Routes>
 {/* Auth routes */}
 <Route path="/login" element={<Login />} />
          <Route path="/demo" element={<Demo />} />
 <Route path="/register" element={<Register />} />
 <Route path="/forgot-password" element={<ForgotPassword />} />
 <Route path="/reset-password" element={<ResetPassword />} />
 <Route path="/onboarding" element={<Onboarding />} />

 {/* Protected routes with dashboard layout */}
 <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>

 {/* Overview (clean: accounts, fetch all, currency, announcements) */}
 <Route path="/dashboard" element={<Dashboard />} />

 {/* Publisher analytics (revenue vs spend, ROAS, charts) */}
 <Route path="/publisher" element={<Publisher />} />

 <Route path="/adsense/callback" element={<AdSenseCallback />} />

 {/* Projects were removed from the product. The tables and rows are
     untouched in the database, but the UI is gone — anything still
     linking here lands on the dashboard rather than a dead route. */}
 <Route path="/projects" element={<Navigate to="/dashboard" replace />} />
 <Route path="/projects/*" element={<Navigate to="/dashboard" replace />} />

 {/* A/B tests, now standalone rather than nested under a project. */}
 <Route path="/ab-tests" element={<AbTests />} />
 <Route path="/ab-tests/:testId" element={<TestDetail />} />
 <Route path="/ab-tests/:testId/analytics" element={<TestAnalytics />} />
 {/* Old bookmarks and in-app links from before the move. */}
 <Route path="/tests/:testId" element={<TestDetail />} />
 <Route path="/tests/:testId/analytics" element={<TestAnalytics />} />

 <Route path="/google-ads" element={<GoogleAds />} />
 <Route path="/tiktok-ads" element={<TikTokAds />} />
 <Route path="/team" element={<Team />} />
 <Route path="/shopify" element={<Shopify />} />
 <Route path="/tiktok-shop" element={<TikTokShop />} />
 <Route path="/ecommerce" element={<EcommerceDashboard />} />
 <Route path="/meta-ads" element={<MetaAds />} />
 <Route path="/adsense" element={<AdSense />} />
 <Route path="/adsense/urls" element={<AdSenseUrls />} />
 <Route path="/ad-manager" element={<AdManager />} />
 <Route path="/ad-manager/urls" element={<AdManagerUrls />} />
 <Route path="/custom-domains" element={<CustomDomains />} />
 <Route path="/custom-accounts" element={<CustomAccounts />} />

 {/* Landing pages — standalone single pages in the GrapesJS builder.
     A page is a one-step funnel, so this is FunnelsList with kind="page"
     and the shared FunnelDetail / FunnelEditor behind it. */}
 <Route path="/landing-pages" element={<FunnelsList kind="page" />} />

 {/* Legacy section-builder pages, kept so already-published pages stay
     editable. Listed under /legacy so the paths can't collide with the
     new list above. */}
 <Route path="/landing-pages/legacy" element={<LandingPages />} />
 <Route path="/landing-pages/legacy/new" element={<LandingPageBuilder />} />
 <Route path="/landing-pages/legacy/:pageId" element={<LandingPageBuilder />} />

 <Route path="/funnels" element={<FunnelsList kind="funnel" />} />
 <Route path="/funnels/:funnelId" element={<FunnelDetail />} />
 <Route path="/results" element={<Results />} />
 <Route path="/affiliate" element={<Affiliate />} />
 <Route path="/support" element={<Support />} />
 <Route path="/subscribe" element={<Subscribe />} />
 <Route path="/settings" element={<Settings />} />
 <Route path="/hapiguard" element={<HapiGuard />} />
 <Route path="/email-marketing" element={<ProtectedRoute requireAdmin><EmailMarketing /></ProtectedRoute>} />

 {/* Admin routes */}
 <Route path="/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
 <Route path="/system-admin" element={<ProtectedRoute requireAdmin><SystemAdmin /></ProtectedRoute>} />
 </Route>

 {/* Full-screen editor — protected but OUTSIDE DashboardLayout */}
 <Route path="/funnels/:funnelId/edit/:stepId" element={
 <ProtectedRoute>
 <Suspense fallback={<EditorFallback />}>
 <FunnelEditor />
 </Suspense>
 </ProtectedRoute>
 } />

 <Route path="/" element={<Navigate to="/dashboard" replace />} />
 <Route path="*" element={<Navigate to="/dashboard" replace />} />
 </Routes>
 </Router>

 <Toaster
 position="top-right"
 toastOptions={{
 duration: 4000,
 style: {
 background:'hsl(var(--card))',
 color:'hsl(var(--card-foreground))',
 border:'1px solid hsl(var(--border))',
 },
 success: { iconTheme: { primary:'hsl(var(--success))', secondary:'white' } },
 error: { iconTheme: { primary:'hsl(var(--destructive))', secondary:'white' } },
 }}
 />
 </SidebarProvider>
 </ThemeProvider>
 </AuthProvider>
 );
}

export default App;
